/**
 * Where Project Sola's own Worker lives.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONE PLACE, AND THE CLIENT CANNOT CHOOSE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The mirror of `worker/src/config/model.ts`, which pins the model and the
 *  endpoint so no request can steer them. This pins the other end for the same
 *  reason: there is no setting, no override and no query parameter that points
 *  the app at a different service. A token pasted into Settings is a credential
 *  for *this* endpoint and nowhere else, and a configurable URL would make
 *  "paste your token" a way to send a week's summary to any host that asked.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  UNSET MEANS THE FEATURE IS UNAVAILABLE, AND THAT IS A CLEAN STATE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Read from a build-time variable rather than committed, because production
 *  and development are two deployments (D-095 §5) and a URL baked into the
 *  source would make the wrong one a code change. **When it is unset the client
 *  reports `featureDisabled`** — the eleventh error category, which D-095 §4
 *  requires to render as a clean state and never as an outage. That is exactly
 *  right here: a build with no Worker configured has no second opinion to offer,
 *  which is a fact about the build rather than a fault in it.
 *
 *  Failing closed also means a misconfigured build cannot fall back to some
 *  default host. There is no default.
 */

/**
 * The configured Worker origin, or `undefined`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  READ THROUGH A FUNCTION, NOT A MODULE CONSTANT
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `import.meta.env.VITE_SOLA_WORKER_URL` is **still a build-time replacement**
 *  — Vite substitutes the literal wherever it appears, so wrapping it in a
 *  function changes nothing about when the value is decided, and no value can be
 *  injected at runtime.
 *
 *  What it does change is that *unset* becomes testable. As a module constant
 *  the value is fixed the first time the module is imported, so a test that
 *  stubs the variable afterwards is stubbing something already read — every
 *  assertion then runs against whichever state happened to load first. Reading
 *  it per call means the `featureDisabled` state, which is the one D-095 §4
 *  cares most about rendering correctly, can actually be exercised.
 *
 *  Trimmed, because a trailing newline in a secret has already cost this project
 *  a session — `GEMINI_ENABLED` was silently off for exactly that reason at
 *  C7.0 phase 2. A `.env` file attracts the same problem, and here the symptom
 *  would be every request failing against a URL that looks correct in the file.
 */
export function workerOrigin(): string | undefined {
  const configured = import.meta.env['VITE_SOLA_WORKER_URL'] as string | undefined
  const trimmed = configured?.trim()
  if (trimmed === undefined || trimmed === '') return undefined
  return trimmed.replace(/\/+$/, '')
}

/** The one endpoint. There is deliberately no general-purpose proxy (D-093 §3). */
export function reviewEndpoint(): string | undefined {
  const origin = workerOrigin()
  return origin === undefined ? undefined : `${origin}/api/review`
}

/**
 * How long the client waits.
 *
 * **Re-exported from the shared contract, never chosen here.** This constant was
 * a hand-written 25 s until C7.0's first live test, justified by a comment that
 * reasoned about *"the Worker's own 20 s timeout"* and added five seconds — while
 * the Worker retried once, making its real worst case about forty. The client
 * could abort fifteen seconds before an answer it had already been charged for.
 *
 * It is now derived from the provider timeout and the attempt count, so the two
 * ends cannot drift apart again. See `contracts/gemini/timeouts.ts`.
 */
export { CLIENT_TIMEOUT_MS } from '~/contracts/gemini/timeouts'
